import { useRef, useEffect, useState, MouseEvent } from 'react'
import cx from 'classnames'

import { ListRowProps } from './Types'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { AppendChildren } from 'screens/Finder/FinderContextTypes'
import { Draggable } from 'react-beautiful-dnd'
import Styles from './ListRow.module.scss'
import { FavoriteButton } from 'common/FavoriteButton/FavoriteButton'
import { IconButton } from 'components/IconButton/IconButton'
import { FileIcon } from '../../FileIcon/FileIcon'
import { RenameInput } from '../../RenameInput/RenameInput'
import { Link } from 'react-router-dom'
import { generatePath, useHistory } from 'react-router-dom'
import { ALL_FILES_PATH } from 'paths'
import { getArticlePath } from 'utils/urlUtils'
import { handbookLink } from 'components/Handbook2_0/util'
import { useHandbook } from 'components/Handbook2_0/hooks'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { ListProperties } from './ListProperties'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { hasModifierKey } from 'utils/hasModifierKey'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { BaseEmoji } from 'types'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { ContextMenuTrigger } from 'almanac-ui/ContextMenuTrigger/ContextMenuTrigger'
import { DocMenuContainer } from 'components/DocMenu/DocMenuContainer'
import { FolderMenuContainer } from 'components/FolderMenu/FolderMenuContainer'
import useDetectTextOverflow from 'hooks/useDetectTextOverflow'
import { FileEmojiPicker } from 'components/FileEmojiPicker/FileEmojiPicker'
import { MediaFileMenu } from 'components/MediaFileMenu/MediaFileMenu'
import { Text } from 'blaze-ui/Text/Text'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { DocMenuDrawer } from 'blaze-components/MobileWriterTopBar/DocMenuDrawer'
import { ProjectMenuDrawer } from 'blaze-components/ProjectMenu/ProjectMenuDrawer'
import { MediaFileMenuDrawer } from 'components/MediaFileMenu/MediaFileMenuDrawer'
import { MoreDots } from 'blaze-ui/icons'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { Checkbox } from '../../Checkbox/Checkbox'
import { useHover } from '@react-aria/interactions'
import { DesignMenuContainer } from 'components/DesignMenu/DesignMenuContainer'
import { DesignMenuDrawer } from 'components/DesignMenu/DesignMenuDrawer'

const ListRow = observer(({ style, data, setOpen, index }: ListRowProps) => {
  const {
    loadMore,
    hasMore,
    onRename,
    openListFile: openFile,
    isSelected,
    handleClick,
    dropDestination,
    openFolderIds,
    dragging,
    onChangeEmoji,
    showExpandButton,
  } = useFinderContext()
  const [renaming, setRenaming] = useState(false)
  const currentHandbook = useHandbook()
  const history = useHistory()
  const openWindow = useOpenWindowSafely()
  const { isMobile } = useResponsiveDesign()
  const { isHovered, hoverProps } = useHover({})
  const { drawersStore } = useRootStore()

  const { data: brandPlan } = useCurrentBrandPlan()
  const { file, isLast, nestingLevel, enablePagination } = data
  const { parentClient, appendSiblings, forceReload, id, handbook } = file
  const docMenuHiddenKeys = [
    'toggleVersionControl',
    'switchView',
    'readHistory',
    'toggleFocus',
    'export',
    'readInsights',
    'publish',
  ]
  const appendChildrenRef = useRef<AppendChildren['appendChildren']>()

  const parentClientRef = useRef(parentClient)
  const appendSiblingsRef = useRef(appendSiblings)

  const initializeClients = () => {
    const { appendChildren } = openFile(file) as AppendChildren
    appendChildrenRef.current = appendChildren
  }

  useEffect(() => {
    if (forceReload) {
      initializeClients()
      loadChildren(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceReload])

  useEffect(() => {
    if (openFolderIds.includes(id)) {
      handleOpen()
      setOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(file.children)])

  const handleOpen = () => {
    if (file.type === 'folder') {
      initializeClients()
      loadChildren(true)
    } else {
      openFile(file)
    }
    setOpen(true)
  }

  const loadChildren = async (nuke: boolean = false) => {
    if (!appendChildrenRef.current) {
      return
    }

    appendChildrenRef.current(nuke)
  }

  const hasMoreSiblings = !!parentClientRef.current?.hasMore

  const loadMoreSiblings = async () => {
    if (!parentClientRef.current || !appendSiblingsRef.current) {
      return
    }
    const response = await parentClientRef.current.loadMore()
    if (response) {
      appendSiblingsRef.current(response, parentClientRef.current)
    }
  }

  const queryParams = file.mention_id ? `?mention_id=${file.mention_id}` : undefined

  const handleRowClick = (e: MouseEvent) => {
    e.preventDefault()
    if (e.button === 0) {
      const modifierKey = hasModifierKey(e)
      if (modifierKey && !e.shiftKey) {
        file.url && openWindow(file.url)
        return
      } else if (!e.shiftKey) {
        history.push({
          pathname: fileLink(),
          search: queryParams,
        })
      }

      handleClick(file, e.shiftKey && !(e.shiftKey && modifierKey))
    }
  }

  const handleRightClick = (e: any) => {
    // Non mention items render a dedicated menu
    if (!file.mention_id) {
      return
    }

    e.stopPropagation()
    handleClick(file, false)
  }

  const fileLink = () => {
    if (file.type === 'article') {
      if (currentHandbook) return handbookLink(currentHandbook, file.slug, true)

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return getArticlePath({ slug: file.slug! })
    }

    if (handbook) return handbookLink(handbook, file.slug, true)

    if (file.url) return file.url

    return generatePath(ALL_FILES_PATH, { workspaceId: file.real_workspace_id, location: id })
  }

  const validDropDestination = file.type === 'folder' && file.has_edit_access && !file.database_mode

  const onEmojiSelect = async (newEmoji?: BaseEmoji | null) => {
    let header_emoji: string | null | undefined = null

    if (file.header_emoji !== newEmoji?.native) header_emoji = newEmoji?.native
    const channel = newEmoji?.native === undefined ? newEmoji?.id : null

    onChangeEmoji(file, header_emoji, channel)
  }

  const { ref: titleRef, isOverflowing } = useDetectTextOverflow<HTMLSpanElement>()

  const folderMenuPopover = (close: () => void) => (
    <FolderMenuContainer
      id={file.id}
      dismiss={close}
      onAction={(key) => {
        if (key === 'rename') {
          setRenaming(true)
        }
        if (key === 'move') {
          return
        }
        close()
      }}
    />
  )

  const docMenuPopover = (close: () => void) => (
    <DocMenuContainer
      menuType="dropdown"
      id={file.id}
      hiddenKeys={new Set(docMenuHiddenKeys)}
      dismiss={close}
      onAction={(key) => {
        if (key === 'rename') {
          setRenaming(true)
        }
        close()
      }}
    />
  )

  const mediaFileMenuPopover = (close: () => void) => {
    return <MediaFileMenu file={file} close={close} />
  }

  const designMenuPopover = (close: () => void) => (
    <DesignMenuContainer
      id={file.id}
      dismiss={close}
      onAction={(key) => {
        if (key === 'rename') {
          setRenaming(true)
        }
        close()
      }}
    />
  )

  return (
    <Draggable
      draggableId={`${file.id}`}
      index={index}
      key={`${file.id}-${file.mention_id || nestingLevel}`}
      isDragDisabled
    >
      {(provided) => (
        <ContextMenuTrigger>
          <div
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            {...hoverProps}
            ref={provided.innerRef}
            style={style}
            className={cx(Styles.root, {
              [Styles.selected]: isSelected(file),
              [Styles.dropDestination]: dropDestination?.id === file.id,
              [Styles.validDropDestination]: validDropDestination,
              [Styles.dragging]: dragging,
            })}
            onContextMenu={handleRightClick}
          >
            <div className={cx(Styles.leftBorder, { [Styles.visible]: isSelected(file) })} />
            <div className={Styles.leftColumn}>
              {!isMobile && (isHovered || isSelected(file)) ? (
                <Checkbox id={file.id} checked={isSelected(file)} visible onClick={() => handleClick(file, false)} />
              ) : (
                <>{!isMobile && <span className={Styles.rowNumber}>{index + 1}</span>}</>
              )}
              <div
                onClick={(e) => {
                  handleRowClick(e)
                  e.stopPropagation()
                }}
              >
                <DropdownTrigger placement="bottom-start" popoverClassName={Styles.emojiPickerPopover}>
                  <IconButton
                    className={cx(Styles.emojiButton, {
                      [Styles.leftMargin]: !showExpandButton,
                    })}
                  >
                    <FileIcon file={file} size={isMobile ? 'md' : 'sm'} brandPlan={brandPlan} />
                  </IconButton>
                  {(close) => (
                    <FileEmojiPicker
                      fileKey={file.id}
                      onSelect={async (emoji: BaseEmoji | null) => {
                        onEmojiSelect(emoji)
                        close()
                      }}
                      withChannels={file.type === 'article'}
                    />
                  )}
                </DropdownTrigger>
              </div>
              {renaming && (
                <RenameInput
                  className={Styles.renameContainer}
                  onRename={onRename}
                  setShowRename={setRenaming}
                  file={file}
                  isEditing={renaming}
                />
              )}
              {!renaming && (
                <Tooltip label={file.title} placement="top" hide={!isOverflowing}>
                  <ConditionalWrapper
                    condition={file.type !== 'media_file'}
                    wrapper={(children) => (
                      <Link className={Styles.titleWrapper} to={{ pathname: fileLink(), search: queryParams }}>
                        {children}
                      </Link>
                    )}
                    elseWrapper={(children) => <div className={Styles.titleWrapper}>{children}</div>}
                  >
                    <span className={Styles.title} ref={titleRef}>
                      <Text lineClamp={1} variant="largeList">
                        {file.title}
                      </Text>
                    </span>
                  </ConditionalWrapper>
                </Tooltip>
              )}
              {!isMobile && (
                <div className={Styles.favoritesButton}>
                  <FavoriteButton
                    key="favoriteButton"
                    className={cx(Styles.favoriteButton, Styles.actionButton)}
                    favoritableId={id}
                    favoritableType={file.type}
                    favoritableTitle={file.title}
                  />
                </div>
              )}

              {isMobile && (
                <div className={Styles.actionsButton}>
                  {file.type === 'article' && (
                    <IconButton
                      icon={MoreDots}
                      aria-label="File Menu"
                      onPress={() =>
                        drawersStore.openDrawer(DocMenuDrawer, {
                          articleId: file.id,
                          hiddenKeys: docMenuHiddenKeys,
                          onRename: () => setRenaming(true),
                        })
                      }
                    />
                  )}
                  {file.type === 'folder' && (
                    <IconButton
                      icon={MoreDots}
                      aria-label="Project Menu"
                      onPress={() =>
                        drawersStore.openDrawer(ProjectMenuDrawer, {
                          folderId: file.id,
                        })
                      }
                    />
                  )}
                  {file.type === 'design' && (
                    <IconButton
                      icon={MoreDots}
                      aria-label="Project Menu"
                      onPress={() =>
                        drawersStore.openDrawer(DesignMenuDrawer, {
                          id: file.id,
                          onRename: () => setRenaming(true),
                        })
                      }
                    />
                  )}
                  {file.type === 'media_file' && (
                    <IconButton
                      icon={MoreDots}
                      onPress={() =>
                        drawersStore.openDrawer(MediaFileMenuDrawer, {
                          file,
                        })
                      }
                    />
                  )}
                </div>
              )}
            </div>
            {!isMobile && <ListProperties file={file} />}
            <div style={{ position: 'absolute', visibility: 'hidden' }}>
              {enablePagination && isLast && nestingLevel === 0 && (
                <InfiniteScroll hasMore={hasMore} loadMore={loadMore} />
              )}
              {enablePagination && isLast && nestingLevel > 0 && (
                <InfiniteScroll hasMore={hasMoreSiblings} loadMore={loadMoreSiblings} />
              )}
            </div>
            <div className={cx(Styles.borderRight, { [Styles.visible]: isSelected(file) })} />
          </div>
          {(close) => {
            if (file.type === 'folder') return folderMenuPopover(close)
            if (file.type === 'article') return docMenuPopover(close)
            if (file.type === 'design') return designMenuPopover(close)
            if (file.type === 'media_file') return mediaFileMenuPopover(close)
          }}
        </ContextMenuTrigger>
      )}
    </Draggable>
  )
})

export { ListRow }
